import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Grid, Paper, Typography,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getUsersByCompany } from 'services/firebaseServices';
import { getProfile } from 'utilities';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { fireStoreDB } from 'services';

export default function SubscriptionOverview({ embedded = false }) {
  const [employees, setEmployees] = useState([]);
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const profile = getProfile();
        if (!profile?.uid) return;

        const q = query(collection(fireStoreDB, 'businesses'), where('userId', '==', profile.uid));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const biz = snap.docs[0].data();
          setBusinessData(biz);
          const result = await getUsersByCompany(biz.companyName);
          setEmployees(result);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const profile = getProfile();
      const paymentId = profile?.paymentId || businessData?.paymentId;
      if (!paymentId) {
        setError('No payment session found. Please contact support.');
        return;
      }
      const res = await fetch('/api/create-billing-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentId, returnUrl: window.location.href }),
      });
      const data = await res.json();
      if (data.url) window.open(data.url, '_blank');
      else throw new Error(data.error || 'Failed to open billing portal');
    } catch (err) {
      setError(err.message);
    } finally {
      setPortalLoading(false);
    }
  };

  const active = employees.filter((e) => e.isActiveSubscription !== false).length;
  const inactive = employees.length - active;
  const cardOrdered = employees.filter((e) => e.cardOrderStatus === 'Registered').length;

  const stats = [
    { icon: <PeopleIcon />, label: 'Total Seats Used', value: employees.length, color: '#3b82f6' },
    { icon: <CheckCircleIcon />, label: 'Active Subscriptions', value: active, color: '#10b981' },
    { icon: <PeopleIcon />, label: 'Inactive / Pending', value: inactive, color: '#f59e0b' },
    { icon: <TrendingUpIcon />, label: 'Cards Ordered', value: cardOrdered, color: '#8b5cf6' },
  ];

  return (
    <Box sx={{ width: '100%', p: embedded ? 3 : { xs: 1, md: 2 } }}>
      {!embedded && (
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937', mb: 3 }}>
          Subscription Overview
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {stats.map(({ icon, label, value, color }) => (
          <Grid item xs={12} sm={6} md={3} key={label}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb', height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
              </Box>
              <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>{label}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', lineHeight: 1 }}>
                {loading ? <CircularProgress size={24} /> : value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Plan details */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb', height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>Plan Details</Typography>
            <Divider sx={{ mb: 2 }} />
            {[
              { label: 'Company', value: businessData?.companyName || '-' },
              { label: 'Industry', value: businessData?.industry || '-' },
              { label: 'Members enrolled', value: loading ? '…' : employees.length },
              { label: 'Subscription status', value: <Chip label="Active" color="success" size="small" sx={{ fontWeight: 600 }} /> },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f3f4f6' }}>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>{value}</Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* Billing management */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb', height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>Manage Billing</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 3, lineHeight: 1.7 }}>
              Use the Stripe billing portal to update your payment method, download receipts, cancel or upgrade your subscription.
            </Typography>
            <Button
              variant="contained"
              endIcon={portalLoading ? <CircularProgress size={16} color="inherit" /> : <OpenInNewIcon />}
              onClick={openBillingPortal}
              disabled={portalLoading}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}
            >
              Open Billing Portal
            </Button>
            <Typography variant="caption" sx={{ display: 'block', color: '#9ca3af', mt: 2 }}>
              You will be redirected to Stripe. Your payment details are handled securely.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
